import React from 'react';
import { fromJS } from 'immutable';
import { twMerge } from 'tailwind-merge';
import { removeQuotedMessage } from 'flow/actions/chat-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { getStyledMessageBody } from 'flow/utils/message-utils';

interface Props {
    chatSessionKey: ChatSessionKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const QuotedMessage = ({ chatSessionKey, videoDark = false }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { hasFeature } = useFeatureFlags();

    const quotedMessage = useAppSelector((state) =>
        state.chats.getIn(['composer', chatSessionKey, 'quotedMessage']),
    );

    if (!quotedMessage) {
        return null;
    }

    return (
        <div className="group animate-slideInFromBottom sticky bottom-1 z-30 m-auto mb-2 w-full max-w-2xl">
            <div
                className={twMerge(
                    'rounded-sm p-3',
                    videoDark ? 'bg-gray-700' : 'bg-gray-200',
                )}
            >
                <IconButton
                    aria-label={i18next.t('modal.close')}
                    onClick={() =>
                        dispatch(removeQuotedMessage(chatSessionKey))
                    }
                >
                    <Icon
                        iconKey="close"
                        className={twMerge(
                            'absolute end-2 top-3',
                            videoDark ? 'text-gray-300' : 'text-gray-700',
                        )}
                    />
                </IconButton>

                <p
                    className={twMerge(
                        'mb-2 text-sm',
                        videoDark ? 'text-gray-200' : 'text-gray-800',
                    )}
                >
                    {i18next.t('message.input.reply_to', {
                        firstname: quotedMessage.getIn([
                            'from',
                            'contact',
                            'firstName',
                        ]),
                    })}
                </p>
                <p
                    className={twMerge(
                        'line-clamp-4 text-sm whitespace-pre-line',
                        videoDark ? 'text-gray-400' : 'text-gray-500',
                    )}
                >
                    {getStyledMessageBody(
                        fromJS(quotedMessage),
                        chatSessionKey,
                        hasFeature(FeatureFlags.MARKDOWN),
                    )}
                </p>
            </div>
        </div>
    );
};

export default QuotedMessage;
